import type { TabsProps } from 'antd';
import {
  IconAlertTriangle,
  IconComponents,
  IconFileText,
  IconInfoCircle,
  IconSettings,
  IconShieldLock,
  IconTerminal2,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Loader } from '~/app/components/common/Loader/Loader';
import { AssetDetailCommandsTab } from '~/app/components/asset-detail/AssetDetailCommandsTab/AssetDetailCommandsTab';
import { AssetDetailComponentsTab } from '~/app/components/asset-detail/AssetDetailComponentsTab';
import { AssetDetailIncidentsTab } from '~/app/components/asset-detail/AssetDetailIncidentsTab';
import { AssetDetailLogsTab } from '~/app/components/asset-detail/AssetDetailLogsTab';
import { AssetDetailOcppAuthTab } from '~/app/components/asset-detail/AssetDetailOcppAuthTab';
import { AssetDetailOcppConfigurationTab } from '~/app/components/asset-detail/AssetDetailOcppConfigurationTab/AssetDetailOcppConfigurationTab';
import { AssetDetailOverviewTab } from '~/app/components/asset-detail/AssetDetailOverviewTab';
import { AssetDetailTabLabel } from '~/app/components/asset-detail/AssetDetailTabLabel';
import type { AssetDetailPageViewProps } from '~/app/components/asset-detail/AssetDetailPage.types';

type UseAssetDetailTabItemsInput = Pick<
  AssetDetailPageViewProps,
  | 'summary'
  | 'asset'
  | 'assetLogs'
  | 'assetModels'
  | 'manufacturerOptions'
  | 'atmmLoading'
  | 'assetTypes'
  | 'form'
  | 'onValuesChangeAction'
  | 'onCoordinatesChangeAction'
  | 'landlordOptions'
  | 'siteForMap'
  | 'sites'
  | 'uploadClientName'
  | 'zones'
  | 'canDownloadBareLogs'
  | 'customFields'
  | 'customFieldsLoading'
  | 'showIncidentsTab'
  | 'showCommandsTab'
  | 'showOcppConfigurationTab'
  | 'showOcppAuthTab'
  | 'canUpdateAsset'
  | 'organizationChargerAuthEnabled'
  | 'organizationOcppSettingsLoading'
>;

export function useAssetDetailTabItems({
  summary,
  asset,
  assetLogs,
  assetModels,
  manufacturerOptions,
  atmmLoading,
  assetTypes,
  form,
  onValuesChangeAction,
  onCoordinatesChangeAction,
  landlordOptions,
  siteForMap,
  sites,
  uploadClientName,
  zones,
  canDownloadBareLogs,
  customFields,
  customFieldsLoading,
  showIncidentsTab = false,
  showCommandsTab = false,
  showOcppConfigurationTab = false,
  showOcppAuthTab = false,
  canUpdateAsset = false,
  organizationChargerAuthEnabled = false,
  organizationOcppSettingsLoading = false,
}: UseAssetDetailTabItemsInput): TabsProps['items'] {
  const { t } = useTranslation('assets');

  return [
    ...(showIncidentsTab
      ? [
          {
            key: 'incidents',
            label: (
              <AssetDetailTabLabel icon={IconAlertTriangle}>
                {t('detail.tabs.incidents', 'Incidents')}
              </AssetDetailTabLabel>
            ),
            children: (
              <AssetDetailIncidentsTab
                organizationId={summary.organizationId}
                assetId={summary.id}
              />
            ),
          },
        ]
      : []),
    {
      key: 'components',
      label: (
        <AssetDetailTabLabel icon={IconComponents}>
          {t('detail.tabs.components', 'Components')}
        </AssetDetailTabLabel>
      ),
      children: (
        <AssetDetailComponentsTab
          organizationId={summary.organizationId}
          assetId={summary.id}
          components={asset?.components ?? []}
        />
      ),
    },
    {
      key: 'logs',
      label: (
        <AssetDetailTabLabel icon={IconFileText}>
          {t('detail.tabs.logs', 'Logs')}
        </AssetDetailTabLabel>
      ),
      children: (
        <AssetDetailLogsTab
          organizationId={summary.organizationId}
          assetId={summary.id}
          assetName={summary.assetName}
          clientName={uploadClientName}
          assetLogs={assetLogs}
          canDownloadBareLogs={canDownloadBareLogs}
        />
      ),
    },
    ...(showCommandsTab
      ? [
          {
            key: 'commands',
            label: (
              <AssetDetailTabLabel icon={IconTerminal2}>
                {t('detail.tabs.commands')}
              </AssetDetailTabLabel>
            ),
            children: (
              <AssetDetailCommandsTab
                organizationId={summary.organizationId}
                assetId={summary.id}
              />
            ),
          },
        ]
      : []),
    ...(showOcppConfigurationTab
      ? [
          {
            key: 'ocpp-configuration',
            label: (
              <AssetDetailTabLabel icon={IconSettings}>
                {t('detail.tabs.ocppConfiguration', 'OCPP Configuration')}
              </AssetDetailTabLabel>
            ),
            children: (
              <AssetDetailOcppConfigurationTab
                organizationId={summary.organizationId}
                assetId={summary.id}
                canRefresh={canUpdateAsset}
              />
            ),
          },
        ]
      : []),
    {
      key: 'details',
      label: (
        <AssetDetailTabLabel icon={IconInfoCircle}>
          {t('detail.tabs.details', 'Details')}
        </AssetDetailTabLabel>
      ),
      children: asset ? (
        <AssetDetailOverviewTab
          asset={asset}
          form={form}
          assetTypes={assetTypes}
          assetModels={assetModels}
          manufacturerOptions={manufacturerOptions}
          atmmLoading={atmmLoading}
          landlordOptions={landlordOptions}
          sites={sites}
          zones={zones}
          siteForMap={siteForMap}
          onValuesChange={onValuesChangeAction}
          onCoordinatesChange={onCoordinatesChangeAction}
          customFields={customFields}
          customFieldsLoading={customFieldsLoading}
        />
      ) : (
        <Loader />
      ),
    },
    ...(showOcppAuthTab
      ? [
          {
            key: 'ocpp-auth',
            label: (
              <AssetDetailTabLabel icon={IconShieldLock}>
                {t('detail.tabs.ocppAuth', 'OCPP Authentication')}
              </AssetDetailTabLabel>
            ),
            children: asset ? (
              <AssetDetailOcppAuthTab
                asset={asset}
                organizationChargerAuthEnabled={organizationChargerAuthEnabled}
                organizationLoading={organizationOcppSettingsLoading}
              />
            ) : (
              <Loader />
            ),
          },
        ]
      : []),
  ];
}
